import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { usePortfolio } from '@/context/PortfolioContext';
import { Save, User, Award, Languages, Briefcase } from 'lucide-react';
import { toast } from 'sonner';
import { normalizeAbout } from '@/lib/adminUtils';

const AdmAbout = () => {
  const { portfolioData, updateAbout } = usePortfolio();
  const [formData, setFormData] = useState(() => normalizeAbout(portfolioData?.about));
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setFormData(normalizeAbout(portfolioData?.about));
  }, [portfolioData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const result = await updateAbout(formData);
      if (result?.success) {
        toast.success('Informations à propos mises à jour');
      } else {
        toast.error(result?.message || 'Erreur lors de la mise à jour des informations à propos');
      }
    } catch (error) {
      toast.error('Erreur lors de la mise à jour des informations à propos');
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    setFormData(normalizeAbout(portfolioData?.about));
    toast.success('Formulaire réinitialisé');
  };

  const handleLanguageChange = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      languages: prev.languages.map((lang, i) =>
        i === index ? { ...lang, [field]: value } : lang
      ),
    }));
  };

  const addLanguage = () => {
    setFormData((prev) => ({
      ...prev,
      languages: [...prev.languages, { name: '', level: '', spoken: false, written: false }],
    }));
  };

  const removeLanguage = (index) => {
    setFormData((prev) => ({
      ...prev,
      languages: prev.languages.filter((_, i) => i !== index),
    }));
  };

  const handleCertificationChange = (index, value) => {
    setFormData((prev) => ({
      ...prev,
      certifications: prev.certifications.map((cert, i) => (i === index ? value : cert)),
    }));
  };

  const addCertification = () => {
    setFormData((prev) => ({
      ...prev,
      certifications: [...prev.certifications, ''],
    }));
  };

  const removeCertification = (index) => {
    setFormData((prev) => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index),
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center">
          <User className="w-6 h-6 mr-2" />
          Section À Propos
        </h2>
        <div className="space-x-2">
          <Button variant="outline" onClick={handleReset}>
            Réinitialiser
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            <Save className="w-4 h-4 mr-2" />
            {isSaving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center">
              <User className="w-5 h-5 mr-2" />
              Description Professionnelle
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="description" className="flex items-center mb-1">Description</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                placeholder="Votre description professionnelle détaillée"
                rows={6}
              />
            </div>
            <div>
              <Label htmlFor="image" className="flex items-center mb-1">Nom de l&apos;image de profil</Label>
              <Input
                id="image"
                value={formData.image}
                onChange={(e) => handleChange('image', e.target.value)}
                placeholder="nom_du_fichier.png"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Award className="w-5 h-5 mr-2" />
              Statistiques
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="yearsOfExperience" className="flex items-center mb-1">Années d&apos;expérience</Label>
              <Input
                id="yearsOfExperience"
                type="number"
                min="0"
                value={formData.yearsOfExperience}
                onChange={(e) => handleChange('yearsOfExperience', parseInt(e.target.value, 10) || 0)}
                placeholder="5"
              />
            </div>
            <div>
              <Label htmlFor="projectsCompleted" className="flex items-center mb-1">Projets complétés</Label>
              <Input
                id="projectsCompleted"
                type="number"
                min="0"
                value={formData.projectsCompleted}
                onChange={(e) => handleChange('projectsCompleted', parseInt(e.target.value, 10) || 0)}
                placeholder="15"
              />
            </div>
            <div>
              <Label htmlFor="clientsSatisfied" className="flex items-center mb-1">Clients satisfaits</Label>
              <Input
                id="clientsSatisfied"
                type="number"
                min="0"
                value={formData.clientsSatisfied}
                onChange={(e) => handleChange('clientsSatisfied', parseInt(e.target.value, 10) || 0)}
                placeholder="10"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Briefcase className="w-5 h-5 mr-2" />
              Certifications
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.certifications.map((certification, index) => (
              <div key={index} className="flex items-center space-x-2">
                <Input
                  value={certification}
                  onChange={(e) => handleCertificationChange(index, e.target.value)}
                  placeholder="Nom de la certification"
                  className="flex-1"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => removeCertification(index)}
                  className="text-red-600 hover:text-red-700"
                >
                  Supprimer
                </Button>
              </div>
            ))}
            <Button variant="outline" onClick={addCertification} className="w-full">
              Ajouter une certification
            </Button>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Languages className="w-5 h-5 mr-2" />
              Langues
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {formData.languages.map((language, index) => (
              <div key={index} className="border rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor={`lang-name-${index}`} className="flex items-center mb-1">Langue</Label>
                    <Input
                      id={`lang-name-${index}`}
                      value={language.name}
                      onChange={(e) => handleLanguageChange(index, 'name', e.target.value)}
                      placeholder="Français"
                    />
                  </div>
                  <div>
                    <Label htmlFor={`lang-level-${index}`} className="flex items-center mb-1">Niveau</Label>
                    <Input
                      id={`lang-level-${index}`}
                      value={language.level}
                      onChange={(e) => handleLanguageChange(index, 'level', e.target.value)}
                      placeholder="Courant"
                    />
                  </div>
                  <div className="flex items-end space-x-2">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`lang-spoken-${index}`}
                        checked={Boolean(language.spoken)}
                        onChange={(e) => handleLanguageChange(index, 'spoken', e.target.checked)}
                      />
                      <Label htmlFor={`lang-spoken-${index}`}>Parlé</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id={`lang-written-${index}`}
                        checked={Boolean(language.written)}
                        onChange={(e) => handleLanguageChange(index, 'written', e.target.checked)}
                      />
                      <Label htmlFor={`lang-written-${index}`}>Écrit</Label>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeLanguage(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      Supprimer
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            <Button variant="outline" onClick={addLanguage} className="w-full">
              Ajouter une langue
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdmAbout;
